import type { LayerType } from "@gettactic/map-builder/src/types";
import React from "react";
import { useEditorSelector, useEditorStore } from "../store/EditorStoreContext";
import type { Rect } from "../types";
import { ShapeNode } from "./ShapeNode";
import { WallFloorFill, WallJunctions } from "./WallDecorations";

export interface DocumentLayerProps {
  /** World-space rectangle currently on screen (plus margin). */
  visibleArea: Rect | null;
}

const LAYER_ORDER: LayerType[] = [
  "floor",
  "background",
  "shapes",
  "spots",
  "labels"
];

/**
 * Lists the shapes to draw, bottom to top: layer type first, then z-order.
 * Culling uses the spatial index so only shapes near the viewport mount;
 * the list itself is memoised on the shapes map, so it is rebuilt on commit
 * and on a viewport change, never per pointer move.
 */
export const DocumentLayer: React.FC<DocumentLayerProps> = ({
  visibleArea
}) => {
  const store = useEditorStore();
  const shapes = useEditorSelector((state) => state.shapes);
  const order = useEditorSelector((state) => state.order);
  const showZones = useEditorSelector((state) => state.ui.showZones);

  const byLayer = React.useMemo(() => {
    const visible = visibleArea
      ? new Set(store.getState().getIndex().query(visibleArea))
      : null;
    const buckets = new Map<LayerType, string[]>();
    for (const layer of LAYER_ORDER) buckets.set(layer, []);
    for (const id of order) {
      const shape = shapes[id];
      if (!shape || shape.hidden) continue;
      if (shape.type === "zone" && !showZones) continue;
      if (visible && !visible.has(id)) continue;
      const bucket = buckets.get(shape.layerType) ?? buckets.get("shapes");
      bucket?.push(id);
    }
    return buckets;
  }, [shapes, order, visibleArea, store, showZones]);

  const render = (layer: LayerType) =>
    byLayer.get(layer)?.map((id) => <ShapeNode key={id} id={id} />);

  return (
    <g data-layer="document">
      <WallFloorFill />
      {render("floor")}
      {render("background")}
      <WallJunctions />
      {render("shapes")}
      {render("spots")}
      {render("labels")}
    </g>
  );
};
